import { useEffect, useState } from "react";

const storageKey = "appnotes:console:theme";
type Theme = "light" | "dark";

function storedTheme(): Theme | null {
  try {
    const value = window.localStorage.getItem(storageKey);
    return value === "light" || value === "dark" ? value : null;
  } catch {
    return null;
  }
}

/** Keep an explicit choice; otherwise follow changes to the operating system theme. */
export function useTheme(): {
  readonly dark: boolean;
  readonly toggle: () => void;
} {
  const [choice, setChoice] = useState<Theme | null>(storedTheme);
  const [systemDark, setSystemDark] = useState(
    () => window.matchMedia("(prefers-color-scheme: dark)").matches,
  );
  const dark = choice === null ? systemDark : choice === "dark";

  useEffect(() => {
    const query = window.matchMedia("(prefers-color-scheme: dark)");
    const changed = () => setSystemDark(query.matches);
    const storageChanged = (event: StorageEvent) => {
      if (event.key === storageKey || event.key === null)
        setChoice(storedTheme());
    };
    changed();
    query.addEventListener("change", changed);
    window.addEventListener("storage", storageChanged);
    return () => {
      query.removeEventListener("change", changed);
      window.removeEventListener("storage", storageChanged);
    };
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    document.documentElement.style.colorScheme = dark ? "dark" : "light";
  }, [dark]);

  return {
    dark,
    toggle: () => {
      const next = dark ? "light" : "dark";
      setChoice(next);
      try {
        window.localStorage.setItem(storageKey, next);
      } catch {
        // The choice still works for this visit when browser storage is denied.
      }
    },
  };
}
